/**
 * The flyer viewer's data and pure helpers. A thumbnail already points at the
 * full-resolution object, so seeing one big needs no second image and no new
 * request: the viewer shows the same URL at its own size.
 */
import { fLong, fRange } from "./format";
import type { SheetKind } from "./sheets";
import type { WallTile } from "./showsPage";
import type { OnThisDay, ShowDetail, ShowRow } from "./types";
import type { UpcomingRow } from "./upcomingPage";

/** One flyer the viewer can show, plus what its bar prints. */
export interface FlyerShot {
  url: string;
  /** The headliner, or what a not-yet-saved photo is for. */
  title: string;
  /** Venue and date, where the flyer belongs to a show or listing. */
  sub?: string;
  /** Where "Show details" goes, for a flyer that belongs to something. */
  sheet?: { kind: SheetKind; value: string | number };
}

/** A reel the viewer steps through, and which of its flyers opened it. */
export interface Reel {
  shots: FlyerShot[];
  at: number;
}

/**
 * Where `by` steps from `at` in a reel of `count`, wrapping at both ends so
 * the last flyer's Next returns to the first. A reel of one never moves.
 */
export function stepTo(at: number, by: number, count: number): number {
  if (count < 1) return 0;
  return (((at + by) % count) + count) % count;
}

/** The shot a reel is showing, or null for a reel with nothing in it. */
export const shotAt = (reel: Reel): FlyerShot | null => reel.shots[reel.at] ?? null;

/** What a screen reader hears in place of the flyer. */
export const shotAlt = (s: FlyerShot): string => [s.title, s.sub].filter(Boolean).join(", ");

/**
 * A reel from a list of rows, opened at `row`. Rows with no flyer are left
 * out, so stepping never lands on an empty slot. It opens on the row itself,
 * not on the first row carrying the same image: shows share a flyer often
 * enough (a festival lineup, a run of nights) that matching by URL would open
 * a neighbour's show and caption it with a neighbour's date. A row that is not
 * in the list opens on its own; a row with no flyer opens nothing.
 */
export function reelFrom<T>(rows: T[], row: T, shotOf: (r: T) => FlyerShot | null): Reel {
  const shots: FlyerShot[] = [];
  let at = -1;
  for (const r of rows) {
    const shot = shotOf(r);
    if (!shot) continue;
    if (r === row) at = shots.length;
    shots.push(shot);
  }
  if (at >= 0) return { shots, at };
  const own = shotOf(row);
  return own ? { shots: [own], at: 0 } : { shots: [], at: 0 };
}

/** The viewer bar's second line: where the show was, and when. */
export const where = (venue: string, date: string) => `${venue} · ${fLong(date)}`;

/**
 * A logged show's flyer. "Show details" goes to its own sheet, except on the
 * Wall, where a flyer several shows share opens the flyer sheet instead.
 */
export function showShot(s: ShowRow, sheet: SheetKind = "show"): FlyerShot | null {
  if (!s.image_url) return null;
  return {
    url: s.image_url,
    title: s.event ?? s.bill[0]?.name ?? `Show #${s.number}`,
    sub: where(s.venue, s.date),
    sheet: { kind: sheet, value: s.id },
  };
}

/** An "On this day" tile's flyer. */
export function pastShot(s: OnThisDay): FlyerShot | null {
  if (!s.image_url) return null;
  return { url: s.image_url, title: s.bands[0] ?? s.venue, sub: where(s.venue, s.date), sheet: { kind: "show", value: s.id } };
}

/** An Upcoming listing's flyer. It has no sheet of its own to open. */
export function listingShot(u: UpcomingRow): FlyerShot | null {
  if (!u.image_url) return null;
  return { url: u.image_url, title: u.bands[0] ?? u.event_name, sub: where(u.venue, u.date) };
}

/** A single flyer on its own: one not saved yet, or one being edited. */
export const loneReel = (url: string, title: string, sub?: string): Reel => ({ shots: [{ url, title, sub }], at: 0 });

/**
 * The show sheet's reel: the main flyer, then each alternate flyer in its
 * stored order, so Next steps through every flyer the show has.
 */
export function showDetailReel(s: ShowDetail): Reel {
  const title = s.event ?? s.bill[0]?.name ?? `Show #${s.number}`;
  const sub = where(s.venue.name, s.date);
  if (!s.image_url) return { shots: [], at: 0 };
  const alts = (s.alt_flyers ?? []).map((a) => ({ url: a.url, title, sub: `Alternate flyer · ${sub}` }));
  return { shots: [...loneReel(s.image_url, title, sub).shots, ...alts], at: 0 };
}

/**
 * The Wall's reel: one shot per tile, in the order the Wall lays them out. A
 * tile several shows share names the count and opens the flyer sheet, which is
 * where that tile's tap used to go.
 */
export function wallReel(tiles: WallTile[], tile: WallTile): Reel {
  return reelFrom(tiles, tile, ({ shows }) => {
    const shot = showShot(shows[0], shows.length > 1 ? "flyer" : "show");
    if (!shot || shows.length < 2) return shot;
    return { ...shot, sub: `${shows.length} shows · ${fRange(shows[shows.length - 1].date, shows[0].date)}` };
  });
}
